import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { forgotPassword } from "../../lib/auth";

export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await forgotPassword(email);
      setSent(true);
      // In dev/test the API hands back the token directly so we can
      // walk the flow without a mailer plumbed in.
      setDevToken(result.reset_token ?? null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 px-6 py-12">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-neutral-900">Reset your password</h1>
        <p className="text-sm text-neutral-600">
          We&apos;ll email you a link to choose a new password.
        </p>
      </header>

      {sent ? (
        <div className="space-y-4 rounded-lg border border-success-200 bg-success-50 p-6 text-sm text-success-700">
          <p>
            If <strong>{email}</strong> has an account, a reset link is on its way.
          </p>
          {devToken !== null && (
            <p className="rounded border border-neutral-300 bg-white p-3 font-mono text-xs text-neutral-700">
              Dev mode: token is <code>{devToken}</code> —{" "}
              <Link to={`/reset-password?email=${encodeURIComponent(email)}`} className="text-primary-600 hover:underline">
                continue
              </Link>
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            Send reset link
          </Button>
        </form>
      )}

      <Link to="/login" className="text-center text-sm text-primary-600 hover:underline">
        Back to sign-in
      </Link>
    </main>
  );
}
